import { Table,  Button, Pagination, Select, Label, Datepicker, ButtonGroup } from "flowbite-react";
import {  HiPlus, HiPencil, HiTrash } from "react-icons/hi";
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from "react";
import useMagazines from "../hooks/useMagazines";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { formatDate } from "../utils/formatters";
import { routes } from "../routes";
import { toast } from 'react-toastify';
import { TableResults } from "../components";
import { useAuth } from "../context/authContext";

interface StateFilters {
    date: string;
    category: number;
    published: number;
    limit: number;
    page: number;
    totalItems: number;
}

const MagazinesPage = () => {
    const { permissions } = useAuth()

    const canCreate = permissions.find(item => item.slug === 'crear-edicion-impresa')
    const canEdit = permissions.find(item => item.slug === 'editar-edicion-impresa')
    const canDelete = permissions.find(item => item.slug === 'borrar-edicion-impresa')
    
    const { loadingMagazines, magazinesError, magazines, totalMagazines, magazineCategories, fetchMagazines, fetchMagazineCategories, deleteMagazine } = useMagazines()

    const categoryRef = useRef<HTMLSelectElement>(null)
    const publishedRef = useRef<HTMLSelectElement>(null)

    const [state, setState] = useReducerAsState<StateFilters>({
        date: new Date().toDateString(),
        category: -1,
        published: 1,
        limit: 20,
        page: 1,
        totalItems: 0,
    })

    const handleDelete = async (id: number) => {
        if(!id) return
        const confirm = window.confirm("¿Deseas eliminar esta edición impresa?")
        if(!confirm) return

        const response = await deleteMagazine(id)
        toast(response ? "Borrado correctamente" : "Ocurrió un error al borrar", {
            type : response ? "success" : "error"
        })
       
        if(response){
            await fetchMagazines({...state})
        }
    }

    useEffect( () => {
        (async () => {
            await fetchMagazineCategories();
        })()
        // eslint-disable-next-line
    },[])

    useEffect(() => {
        (async () => {
            await fetchMagazines({...state})
        })()
        // eslint-disable-next-line
    } ,[state.page])

    const navigate = useNavigate();

    const onPageChange = (page: number) => setState({page});

    const handleCreateNote = () => navigate(`${routes.DASHBOARD}/${routes.MAGAZINES}/create`)

    const handleCalendarChange = (e:any) => setState({date: e})

    const handleFilter = async () => {
        const categoryId = categoryRef.current ? parseInt(categoryRef.current.value) :  -1
        const published = publishedRef.current ? parseInt(publishedRef.current.value) : -1

        setState({category: categoryId, published})

        await fetchMagazines({
            date: state.date,
            category: categoryId,
            published: published,
            limit: state.limit,
            page: state.page
        });
    }

    return <div className="m-4">
        <div className="flex flex-col mb-4">
            <div className="mb-4">
                <h1>Edición Impresa</h1>
            </div>
            <div className="flex flex-row justify-between items-end">
                <div className="flex flex-row justify-between items-end space-x-4">
                    <Datepicker 
                        language="es-ES" 
                        labelTodayButton="Hoy" 
                        labelClearButton="Limpiar"
                        onSelectedDateChanged={handleCalendarChange}
                    />
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="categories" value="Categoría" />
                        </div>
                        <Select id="categories" required ref={categoryRef}>
                            <option value="-1">- Elija una opción -</option>
                            {magazineCategories && magazineCategories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </div>
                    <div className="max-w-md">
                        <div className="mb-2 block">
                            <Label htmlFor="published" value="Publicado" />
                        </div>
                        <Select ref={publishedRef} id="published" required>
                            <option value="1">Si</option>
                            <option value="0">No</option>
                        </Select>
                    </div>
                    <div className="mb-0">
                        <Button onClick={handleFilter}>Filtrar</Button>
                    </div>
                </div>
                <div className="flex flex-row">
                    {canCreate && <Button onClick={handleCreateNote}>
                        <HiPlus className="mr-2 h-5 w-5" />
                        Crear edición impresa
                    </Button>}
                </div>
            </div>
        </div>
        <TableResults
            loading={loadingMagazines}
            error={magazinesError}
            itemsCount={totalMagazines}
        >
            <div className="overflow-x-auto mb-4 w-full bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                <Table>
                    <Table.Head>
                        <Table.HeadCell>Título</Table.HeadCell>
                        <Table.HeadCell>Publicado</Table.HeadCell>
                        <Table.HeadCell>Creado</Table.HeadCell>
                        <Table.HeadCell>
                            <span className="sr-only">Editar</span>
                        </Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                        {magazines && magazines.map((item) => <Table.Row key={item.id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                            <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                {item.name}
                            </Table.Cell>
                            <Table.Cell>{item.published === 1 ? "Si" : "No"}</Table.Cell>
                            <Table.Cell>{formatDate(item.created_at)}</Table.Cell>
                            <Table.Cell>
                                <ButtonGroup>
                                    {canEdit && <Button onClick={() => navigate(`${routes.DASHBOARD}/${routes.MAGAZINES}/${item.id}`)} color="gray">
                                        <HiPencil />
                                    </Button>}
                                    {canDelete && <Button onClick={() => handleDelete(item.id)} color="gray">
                                        <HiTrash />
                                    </Button>}
                                </ButtonGroup>
                            </Table.Cell>
                        </Table.Row>)}
                    </Table.Body>
                </Table>
            </div>
        </TableResults>
        <div className="flex overflow-x-auto sm:justify-center">
            <Pagination currentPage={state.page || 1} totalPages={Math.ceil(totalMagazines / state.limit)} onPageChange={onPageChange} />
        </div>
    </div>
}

export default MagazinesPage;